import qs from 'qs';

import { client } from '@api/client';
import { URL } from '@api/urls';

import { IGroupOperationDTO } from '@interfaces/common/defaultDTO';
import { IDefaultUploadUrlApi, IDownloadUrl } from '@interfaces/common/file';
import { IItemsResponse } from '@interfaces/common/response';
import {
	IAttachPortalFilesDTO,
	ICheckFolderNameDTO,
	ICreateFolderDTO,
	IDeleteFolderDTO,
	IDeletePortalFilesDTO,
	IDownloadFolderDTO,
	IDownloadFoldersDTO,
	IDownloadPortalFileDTO,
	IDownloadPortalFilesDTO,
	IFolderMoveDestinations,
	IGetFilesMilestonesToLinkDTO,
	IGetFoldersDTO,
	IGetPortalFilesDTO,
	IMoveFolderDTO,
	IMovePortalFilesDTO,
	IPortalFileDTO,
	IRenameFolderDTO,
	IUploadFilesDTO,
} from '@interfaces/portal/dto/foldersManagementDTO';
import { IPortalFilesMilestone, IPortalFilesMilestoneToLink, IPortalFolder } from '@interfaces/portal/folders';
import { IProjectUser } from '@interfaces/portal/projects';

const {
	portalFolders,
	portalCreateFolder,
	portalCheckFolderName,
	portalFiles,
	portalFilesUsers,
	portalFilesMilestones,
	portalFilesMilestonesFilter,
	portalFilesUploadUrlAttachment,
	portalUploadFiles,
	portalDeleteFiles,
	portalAttachFiles,
	portalDownloadFiles,
	portalDownloadFolders,
	portalMoveFiles,
} = URL;

export const getPortalFolders = ({
	projectId,
	searchValue,
	milestoneIds,
}: IGetFoldersDTO): Promise<IPortalFolder[]> => {
	return client.get(portalFolders, {
		params: {
			projectId,
			searchValue,
			milestoneIds,
		},
		paramsSerializer: params =>
			qs.stringify(
				Object.fromEntries(
					Object.entries(params).filter(([_, value]) => value !== null && value !== undefined),
				),
				{ indices: false },
			),
	});
};

export const createFolder = (data: ICreateFolderDTO): Promise<IPortalFolder> => {
	return client.post(portalCreateFolder, data);
};

export const checkFolderName = (data: ICheckFolderNameDTO): Promise<void> => {
	return client.post(portalCheckFolderName, data);
};

export const deleteFolder = (data: IDeleteFolderDTO): Promise<void> => {
	return client.post(`${portalFolders}/${data.folderId}/delete`, {});
};

export const renameFolder = (data: IRenameFolderDTO): Promise<void> => {
	return client.post(`${portalFolders}/${data.folderId}/rename`, {
		name: data.folderName,
	});
};

export const getFolderMoveDestinations = (folderId: string): Promise<IFolderMoveDestinations> => {
	return client.get(`${portalFolders}/${folderId}/move-destinations`);
};

export const moveFolder = (data: IMoveFolderDTO): Promise<void> => {
	return client.post(`${portalFolders}/${data.folderId}/move`, {
		parentFolderId: data.destinationFolderId,
	});
};

export const downloadFolder = (data: IDownloadFolderDTO): Promise<void> => {
	return client.post(`${portalFolders}/${data.folderId}/download`, {});
};

export const downloadFolders = ({ projectId, searchValue }: IDownloadFoldersDTO): Promise<void> => {
	return client.post(portalDownloadFolders, {
		projectId,
		searchValue,
	});
};

export const getPortalFiles = ({
	folderId,
	page,
	pageSize,
	sortDirection,
	sortFieldName,
	searchValue,
	milestoneIds,
}: IGetPortalFilesDTO): Promise<IItemsResponse<IPortalFileDTO>> => {
	return client.get(portalFiles, {
		params: {
			page,
			pageSize,
			sortDirection,
			sortFieldName,
			searchValue,
			folderId,
			milestoneIds,
		},
		paramsSerializer: params =>
			qs.stringify(
				Object.fromEntries(
					Object.entries(params).filter(([_, value]) => value !== null && value !== undefined),
				),
				{ indices: false },
			),
	});
};

export const getPortalFilesUsers = ({ projectId }: { projectId: string }): Promise<IProjectUser[]> => {
	return client.get(portalFilesUsers, {
		params: {
			projectId,
		},
	});
};

export const getPortalFilesMilestones = ({ projectId }: { projectId: string }): Promise<IPortalFilesMilestone[]> => {
	return client.get(portalFilesMilestones, {
		params: {
			projectId,
		},
	});
};

export const getPortalFilesMilestonesFilter = ({
	projectId,
}: { projectId: string }): Promise<IPortalFilesMilestone[]> => {
	return client.get(portalFilesMilestonesFilter, {
		params: {
			projectId,
		},
	});
};

export const getFilesMilestonesToLink = ({
	fileId,
	projectId,
}: IGetFilesMilestonesToLinkDTO): Promise<IPortalFilesMilestoneToLink[]> => {
	return client.get(`${portalFiles}/${fileId}/milestones`, {
		params: {
			projectId,
		},
	});
};

export const getFilesUploadURLAttachment = ({ fileName }: IDefaultUploadUrlApi) => {
	return client.post(portalFilesUploadUrlAttachment, {
		fileName,
	});
};

export const uploadFiles = (data: IUploadFilesDTO): Promise<void> => {
	return client.post(portalUploadFiles, data);
};

export const removePortalFiles = ({
	filter,
	ids,
	isAll,
}: IGroupOperationDTO<IDeletePortalFilesDTO | null>): Promise<void> => {
	return client.post(portalDeleteFiles, {
		filter,
		ids,
		isAll,
	});
};

export const attachPortalFiles = ({
	ids,
	milestoneIds,
	isAll,
	filter,
}: IAttachPortalFilesDTO): Promise<void> => {
	return client.post(portalAttachFiles, {
		ids,
		milestoneIds,
		isAll,
		filter,
	});
};

export const downloadPortalFiles = ({
	filter,
	ids,
	isAll,
}: IGroupOperationDTO<IDownloadPortalFilesDTO | null>): Promise<void> => {
	return client.post(portalDownloadFiles, {
		filter,
		ids,
		isAll,
	});
};

export const downloadPortalFile = ({ fileId }: IDownloadPortalFileDTO): Promise<IDownloadUrl> => {
	return client.post(`${portalFiles}/${fileId}/download`, {});
};

export const movePortalFiles = (data: IMovePortalFilesDTO): Promise<void> => {
	return client.post(portalMoveFiles, data);
};
